import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChairOutlined,
  ImageNotSupportedOutlined,
  KingBedOutlined,
  KitchenOutlined,
  PersonOutlineOutlined,
  PhoneOutlined,
  PhotoLibraryOutlined,
  PlaceOutlined,
  StairsOutlined,
  ThermostatOutlined,
} from "@mui/icons-material";
import PhotoImage from "./PhotoImage";
import { loadAlbum, subscribeAlbum } from "../albumPhotos";
import formatTableDate from "../utils/dates";
import {
  districtLabel,
  floorLabel,
  formatPrice,
  listingTitle,
  roomsLabel,
} from "../public/utils/format";
import { radius, shadow, tokens } from "../theme/theme";
import { CHRISTMAS, HALLOWEEN } from "../public/halloween/season";
import CardSpooks, { CARD_SPOOK_HOVER } from "../public/halloween/CardSpooks";
import CardFrost, { CARD_FROST_HOVER } from "../public/christmas/CardFrost";

/**
 * One listing as a card, for the realtor panel.
 *
 * The photo half is deliberately the same as the public ListingCard — a realtor
 * recognising a flat from its cover is the same act as a customer doing it, and
 * two different-looking answers to "what does this flat look like" would be a
 * bug, not a feature.
 *
 * Everything else is the panel's own, and is what the table row carries rather
 * than what the public card carries:
 *
 *   • the reference number instead of the public "Нове" badge — this list is
 *     already sorted newest-first, so a "new" flag marks whatever happens to be
 *     at the top and tells nobody anything, while «№ 254133» is how a listing
 *     gets named on the phone;
 *   • стан and опалення, which the public card leaves out;
 *   • the contact, because the first question about any row here is whose it
 *     is — the table has had those two columns all along;
 *   • the currency exactly as typed, when it is more than a code;
 *   • the row actions, in the table's order.
 */

function Feature({ icon, children }) {
  if (!children) return null;
  return (
    <Stack
      direction="row"
      spacing={0.6}
      alignItems="center"
      sx={{ color: tokens.ink500 }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 16 } })}
      <Typography
        variant="caption"
        sx={{ color: "inherit", whiteSpace: "nowrap" }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

/**
 * The half of the id a realtor actually says out loud.
 *
 * Ids are `<random>-<timestamp>`; the timestamp half is there to keep them
 * unique and is noise on screen.
 */
function shortId(id) {
  return String(id || "").split("-")[0];
}

/** Objects carry dateEdit, archives carry dateDeleted. Show whichever exists. */
function secondaryDate(item) {
  if (item.dateEdit) return "ред. " + formatTableDate(item.dateEdit);
  if (item.dateDeleted) return "в архіві " + formatTableDate(item.dateDeleted);
  return "";
}

export default function ObjectCard({ item, s3Client, actions, onOpen }) {
  const [photos, setPhotos] = useState(null);

  useEffect(
    function () {
      var alive = true;

      function read() {
        loadAlbum(s3Client, item.id, item.photoOrder).then(function (result) {
          if (alive) setPhotos(result);
        });
      }

      read();
      // A listing is added to the list before its photos finish uploading, so
      // this first read comes back empty. invalidateAlbum() fires when the
      // upload lands and we read again — otherwise the card would say
      // "Без фото" until the page was reloaded.
      const unsubscribe = subscribeAlbum(item.id, read);

      return function () {
        alive = false;
        unsubscribe();
      };
    },
    [s3Client, item.id, item.photoOrder]
  );

  const cover = photos && photos.length > 0 ? photos[0] : null;
  const second = secondaryDate(item);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition:
          "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: shadow.md,
          borderColor: tokens.blue100,
        },
        ...(HALLOWEEN ? CARD_SPOOK_HOVER : CHRISTMAS ? CARD_FROST_HOVER : {}),
      }}
    >
      <Box
        onClick={
          onOpen
            ? function () {
                onOpen(item);
              }
            : undefined
        }
        sx={{
          position: "relative",
          aspectRatio: "4 / 3",
          backgroundColor: tokens.line2,
          overflow: "hidden",
          cursor: onOpen ? "pointer" : "default",
        }}
      >
        {photos === null && (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        )}

        {photos !== null && cover && (
          <PhotoImage
            src={cover.thumbUrl}
            fallbackSrc={cover.url}
            alt={listingTitle(item)}
            loading="lazy"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}

        {photos !== null && !cover && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", color: tokens.ink400, gap: 0.5 }}
          >
            <ImageNotSupportedOutlined sx={{ fontSize: 30 }} />
            <Typography variant="caption">Без фото</Typography>
          </Stack>
        )}

        {/* Before the badges, so the snow settles behind them, not over them. */}
        {CHRISTMAS && <CardFrost />}

        <Tooltip title={String(item.id)}>
          <Chip
            size="small"
            label={"№ " + shortId(item.id)}
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              backgroundColor: "rgba(14,27,46,0.72)",
              color: "#fff",
              backdropFilter: "blur(4px)",
              fontVariantNumeric: "tabular-nums",
            }}
          />
        </Tooltip>

        {photos && photos.length > 1 && (
          <Chip
            size="small"
            icon={
              <PhotoLibraryOutlined
                sx={{ fontSize: 15, color: "#fff !important" }}
              />
            }
            label={photos.length}
            sx={{
              position: "absolute",
              bottom: 10,
              right: 10,
              backgroundColor: "rgba(14,27,46,0.72)",
              color: "#fff",
              backdropFilter: "blur(4px)",
              "& .MuiChip-label": { pl: 0.5, pr: 1 },
            }}
          />
        )}

        {HALLOWEEN && <CardSpooks />}

        <Box
          sx={{
            position: "absolute",
            bottom: 10,
            left: 10,
            px: 1.4,
            py: 0.6,
            borderRadius: radius.sm,
            backgroundColor: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(6px)",
            boxShadow: shadow.sm,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, lineHeight: 1.2 }}
          >
            {formatPrice(item.cost, item.currency)}
          </Typography>
          {/* The raw currency string is free text a realtor typed —
              «$ по курсу НБУ» and friends. formatPrice() normalises it to a
              symbol, so the original is worth showing here and nowhere else. */}
          {item.currency && String(item.currency).length > 3 && (
            <Typography
              variant="caption"
              sx={{
                color: tokens.ink500,
                display: "block",
                lineHeight: 1.1,
              }}
            >
              {item.currency}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          p: 2,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.35 }}
        >
          {listingTitle(item)}
        </Typography>

        <Stack direction="row" spacing={0.6} alignItems="center">
          <PlaceOutlined sx={{ fontSize: 16, color: tokens.blue600 }} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {districtLabel(item.region) || "Львів"}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1.6} flexWrap="wrap" useFlexGap>
          <Feature icon={<KingBedOutlined />}>{roomsLabel(item.rooms)}</Feature>
          <Feature icon={<StairsOutlined />}>{floorLabel(item.floor)}</Feature>
          <Feature icon={<ThermostatOutlined />}>{item.heating}</Feature>
          {item.furniture ? <Feature icon={<ChairOutlined />}>Меблі</Feature> : null}
          {item.technology ? (
            <Feature icon={<KitchenOutlined />}>Техніка</Feature>
          ) : null}
        </Stack>

        {item.state ? (
          <Chip
            size="small"
            label={item.state}
            sx={{
              alignSelf: "flex-start",
              backgroundColor: tokens.blue50,
              color: tokens.blue700,
              fontWeight: 600,
            }}
          />
        ) : null}

        {(item.personName || item.phoneNumber) && (
          <Stack
            direction="row"
            spacing={1.4}
            flexWrap="wrap"
            useFlexGap
            sx={{
              mt: "auto",
              pt: 1,
              borderTop: "1px dashed " + tokens.line,
            }}
          >
            {item.personName ? (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <PersonOutlineOutlined sx={{ fontSize: 16, color: tokens.ink400 }} />
                <Typography variant="caption" sx={{ color: tokens.ink500 }} noWrap>
                  {item.personName}
                </Typography>
              </Stack>
            ) : null}
            {item.phoneNumber ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PhoneOutlined sx={{ fontSize: 16, color: tokens.ink400 }} />
                {/* A tel: link, because half of this panel is used on a phone. */}
                <Link
                  href={"tel:" + String(item.phoneNumber).replace(/[^\d+]/g, "")}
                  variant="caption"
                  underline="hover"
                  onClick={function (event) {
                    event.stopPropagation();
                  }}
                  sx={{ color: tokens.blue700, whiteSpace: "nowrap" }}
                >
                  {item.phoneNumber}
                </Link>
              </Stack>
            ) : null}
          </Stack>
        )}

        <Typography
          variant="caption"
          sx={{
            color: tokens.ink400,
            mt: item.personName || item.phoneNumber ? 0 : "auto",
            pt: 0.5,
          }}
        >
          Додано {formatTableDate(item.dateFrom)}
          {second ? " · " + second : ""}
        </Typography>
      </Box>

      {actions && actions.length > 0 && (
        <React.Fragment>
          <Divider />
          <Stack direction="row" spacing={0.5} sx={{ px: 1, py: 0.5 }}>
            {actions.map(function (action) {
              return (
                <Tooltip key={action.key} title={action.label}>
                  <IconButton
                    size="small"
                    aria-label={action.label}
                    onClick={function () {
                      action.onClick(item);
                    }}
                    sx={{ color: action.color || tokens.ink500 }}
                  >
                    {action.icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Stack>
        </React.Fragment>
      )}
    </Card>
  );
}
